import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import * as fromSeriousSystem from '@serious-system';

/**
 * Content for a confirm dialog — all strings already resolved by the caller
 * (this component is i18n-free, like the rest of the Serious System modal
 * surface). `confirmType` drives the confirm button's emphasis: `delete` for a
 * destructive/data-losing action (red), `save` (default) for a neutral primary.
 */
export interface ConfirmDialogOptions {
  title: string;
  message: string;
  /** A part of `message` to set in bold, such as the name of what gets deleted. Stays plain text. */
  emphasis?: string;
  confirmLabel: string;
  cancelLabel: string;
  confirmType?: 'save' | 'delete';
}

/**
 * Generic confirm dialog rendered as an `sd-modal` (same chrome as the delete
 * confirmations). Driven by `ConfirmDialogService`, which hosts a single
 * instance at the app root and resolves the caller's observable from `choose`.
 */
@Component({
  selector: 'squadbox-confirm-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [fromSeriousSystem.ModalComponent],
  template: `
    <sd-modal
      [isModalVisible]="true"
      [modalOptions]="modalOptions()"
      (triggerAction)="onAction($event)"
    >
      @let parts = messageParts();
      <p class="typo-p2 text-neutral-700">
        {{ parts.before
        }}<strong class="font-semibold text-neutral-900">{{
          parts.emphasis
        }}</strong
        >{{ parts.after }}
      </p>
    </sd-modal>
  `,
})
export class ConfirmDialogComponent {
  public readonly options = input.required<ConfirmDialogOptions>();
  /** Emits `true` on confirm, `false` on cancel / backdrop dismiss. */
  public readonly choose = output<boolean>();

  protected readonly messageParts = computed(() => {
    const { message, emphasis } = this.options();
    const at = emphasis ? message.indexOf(emphasis) : -1;
    return at < 0 || !emphasis
      ? { before: message, emphasis: '', after: '' }
      : {
          before: message.slice(0, at),
          emphasis,
          after: message.slice(at + emphasis.length),
        };
  });

  protected modalOptions(): fromSeriousSystem.Modals {
    const options = this.options();
    return {
      title: options.title,
      width: 480,
      showCloseButton: false,
      backdropDismiss: true,
      actions: [
        {
          label: options.cancelLabel,
          action: 'cancel',
          type: 'cancel',
          disabled: false,
        },
        {
          label: options.confirmLabel,
          action: 'confirm',
          type: options.confirmType ?? 'save',
          disabled: false,
        },
      ],
    };
  }

  protected onAction(action: string): void {
    this.choose.emit(action === 'confirm');
  }
}
